import { createSlice } from '@reduxjs/toolkit';
import api from 'api';
import { transformFiltersToQuery } from './utils/transformFilterToQuery';

const createEntityState = () => ({
    pending: false,
    savePending: false,
    items: [],
    error: null,
    currentPage: 0,
    totalPages: 0,
    count: 0,
    total: 0,
});

export const integrationSlice = createSlice({
    name: 'integration',
    initialState: {
        integrations: createEntityState(),
        channels: createEntityState(),
        jobs: createEntityState(),
        functions: createEntityState(),
        enums: createEntityState(),
        deployments: createEntityState(),
        deploymentsCount: 0,
        tags: [],
    },
    reducers: {
        startIntegrations: (state) => {
            state.integrations.pending = true;
        },
        stopIntegrations: (state) => {
            state.integrations.pending = false;
        },
        setIntegrations: (state, action) => {
            state.integrations.pending = false;
            state.integrations.items = action.payload.items;
            state.integrations.currentPage = action.payload.currentPage;
            state.integrations.totalPages = action.payload.totalPages;
            state.integrations.count = action.payload.count;
            state.integrations.total = action.payload.total;
        },
        setIntegrationsError: (state, action) => {
            state.integrations.pending = false;
            state.integrations.error = action.payload;
        },
        addIntegration: (state, action) => {
            state.integrations.savePending = false;
            state.integrations.items = [...state.integrations.items, action.payload];
        },
        editIntegration: (state, action) => {
            state.integrations.savePending = false;
            const index = state.integrations.items.findIndex((integration) => integration.id === action.payload.id);
            if (index !== -1) {
                state.integrations.items = [
                    ...state.integrations.items.slice(0, index),
                    { ...state.integrations.items[index], ...action.payload },
                    ...state.integrations.items.slice(index + 1),
                ];
            }
        },
        deleteIntegration: (state, action) => {
            state.integrations.pending = false;
            const index = state.integrations.items.findIndex((integration) => integration.id === action.payload.id);
            if (index !== -1) {
                state.integrations.items = [
                    ...state.integrations.items.slice(0, index),
                    ...state.integrations.items.slice(index + 1),
                ];
            }
        },
        setIntegrationTags: (state, action) => {
            state.tags = action.payload;
        },
        resetIntegrations: (state) => {
            state.integrations = createEntityState();
        },
        startSaveIntegrations: (state) => {
            state.integrations.savePending = true;
        },
        stopSaveIntegrations: (state) => {
            state.integrations.savePending = false;
        },
        setSaveIntegrationsError: (state, action) => {
            state.integrations.savePending = false;
            state.integrations.error = action.payload;
        },

        startIntegrationChannels: (state) => {
            state.channels.pending = true;
        },
        stopIntegrationChannels: (state) => {
            state.channels.pending = false;
        },
        setIntegrationChannels: (state, action) => {
            state.channels.pending = false;
            state.channels.items = action.payload.items;
            state.channels.currentPage = action.payload.currentPage;
            state.channels.totalPages = action.payload.totalPages;
            state.channels.count = action.payload.count;
            state.channels.total = action.payload.total;
        },
        setIntegrationChannelsError: (state, action) => {
            state.channels.pending = false;
            state.channels.error = action.payload;
        },
        addIntegrationChannel: (state, action) => {
            state.channels.savePending = false;
            state.channels.items = [...state.channels.items, action.payload];
        },
        editIntegrationChannel: (state, action) => {
            state.channels.savePending = false;
            const index = state.channels.items.findIndex((item) => item.id === action.payload.id);
            if (index !== -1) {
                state.channels.items = [
                    ...state.channels.items.slice(0, index),
                    { ...state.channels.items[index], ...action.payload },
                    ...state.channels.items.slice(index + 1),
                ];
            }
        },
        deleteIntegrationChannel: (state, action) => {
            state.channels.pending = false;
            const index = state.channels.items.findIndex((item) => item.id === action.payload.id);
            if (index !== -1) {
                state.channels.items = [
                    ...state.channels.items.slice(0, index),
                    ...state.channels.items.slice(index + 1),
                ];
            }
        },
        resetIntegrationChannels: (state) => {
            state.channels = createEntityState();
        },
        startSaveIntegrationChannels: (state) => {
            state.channels.savePending = true;
        },
        stopSaveIntegrationChannels: (state) => {
            state.channels.savePending = false;
        },
        setSaveIntegrationChannelsError: (state, action) => {
            state.channels.savePending = false;
            state.channels.error = action.payload;
        },

        startIntegrationJobs: (state) => {
            state.jobs.pending = true;
        },
        stopIntegrationJobs: (state) => {
            state.jobs.pending = false;
        },
        setIntegrationJobs: (state, action) => {
            state.jobs.pending = false;
            state.jobs.items = action.payload.items;
            state.jobs.currentPage = action.payload.currentPage;
            state.jobs.totalPages = action.payload.totalPages;
            state.jobs.count = action.payload.count;
            state.jobs.total = action.payload.total;
        },
        setIntegrationJobsError: (state, action) => {
            state.jobs.pending = false;
            state.jobs.error = action.payload;
        },
        addIntegrationJob: (state, action) => {
            state.jobs.savePending = false;
            state.jobs.items = [...state.jobs.items, action.payload];
        },
        editIntegrationJob: (state, action) => {
            state.jobs.savePending = false;
            const index = state.jobs.items.findIndex((item) => item.id === action.payload.id);
            if (index !== -1) {
                state.jobs.items = [
                    ...state.jobs.items.slice(0, index),
                    { ...state.jobs.items[index], ...action.payload },
                    ...state.jobs.items.slice(index + 1),
                ];
            }
        },
        deleteIntegrationJob: (state, action) => {
            state.jobs.pending = false;
            const index = state.jobs.items.findIndex((item) => item.id === action.payload.id);
            if (index !== -1) {
                state.jobs.items = [...state.jobs.items.slice(0, index), ...state.jobs.items.slice(index + 1)];
            }
        },
        resetIntegrationJobs: (state) => {
            state.jobs = createEntityState();
        },
        startSaveIntegrationJobs: (state) => {
            state.jobs.savePending = true;
        },
        stopSaveIntegrationJobs: (state) => {
            state.jobs.savePending = false;
        },
        setSaveIntegrationJobsError: (state, action) => {
            state.jobs.savePending = false;
            state.jobs.error = action.payload;
        },

        startIntegrationFunctions: (state) => {
            state.functions.pending = true;
        },
        stopIntegrationFunctions: (state) => {
            state.functions.pending = false;
        },
        setIntegrationFunctions: (state, action) => {
            state.functions.pending = false;
            state.functions.items = action.payload.items;
            state.functions.currentPage = action.payload.currentPage;
            state.functions.totalPages = action.payload.totalPages;
            state.functions.count = action.payload.count;
            state.functions.total = action.payload.total;
        },
        setIntegrationFunctionsError: (state, action) => {
            state.functions.pending = false;
            state.functions.error = action.payload;
        },
        addIntegrationFunction: (state, action) => {
            state.functions.savePending = false;
            state.functions.items = [...state.functions.items, action.payload];
        },
        editIntegrationFunction: (state, action) => {
            state.functions.savePending = false;
            const index = state.functions.items.findIndex((item) => item.id === action.payload.id);
            if (index !== -1) {
                state.functions.items = [
                    ...state.functions.items.slice(0, index),
                    { ...state.functions.items[index], ...action.payload },
                    ...state.functions.items.slice(index + 1),
                ];
            }
        },
        deleteIntegrationFunction: (state, action) => {
            state.functions.pending = false;
            const index = state.functions.items.findIndex((item) => item.id === action.payload.id);
            if (index !== -1) {
                state.functions.items = [
                    ...state.functions.items.slice(0, index),
                    ...state.functions.items.slice(index + 1),
                ];
            }
        },
        resetIntegrationFunctions: (state) => {
            state.functions = createEntityState();
        },
        startSaveIntegrationFunctions: (state) => {
            state.functions.savePending = true;
        },
        stopSaveIntegrationFunctions: (state) => {
            state.functions.savePending = false;
        },
        setSaveIntegrationFunctionsError: (state, action) => {
            state.functions.savePending = false;
            state.functions.error = action.payload;
        },

        startOrganizationEnums: (state) => {
            state.enums.pending = true;
        },
        stopOrganizationEnums: (state) => {
            state.enums.pending = false;
        },
        setOrganizationEnums: (state, action) => {
            state.enums.pending = false;
            state.enums.items = action.payload.items;
            state.enums.currentPage = action.payload.currentPage;
            state.enums.totalPages = action.payload.totalPages;
            state.enums.count = action.payload.count;
            state.enums.total = action.payload.total;
        },
        setOrganizationEnumsError: (state, action) => {
            state.enums.pending = false;
            state.enums.error = action.payload;
        },
        addOrganizationEnum: (state, action) => {
            state.enums.savePending = false;
            state.enums.items = [...state.enums.items, action.payload];
        },
        editOrganizationEnum: (state, action) => {
            state.enums.savePending = false;
            const index = state.enums.items.findIndex((item) => item.id === action.payload.id);
            if (index !== -1) {
                state.enums.items = [
                    ...state.enums.items.slice(0, index),
                    { ...state.enums.items[index], ...action.payload },
                    ...state.enums.items.slice(index + 1),
                ];
            }
        },
        deleteOrganizationEnum: (state, action) => {
            state.enums.pending = false;
            const index = state.enums.items.findIndex((item) => item.id === action.payload.id);
            if (index !== -1) {
                state.enums.items = [...state.enums.items.slice(0, index), ...state.enums.items.slice(index + 1)];
            }
        },
        resetOrganizationEnums: (state) => {
            state.enums = createEntityState();
        },
        startSaveOrganizationEnums: (state) => {
            state.enums.savePending = true;
        },
        stopSaveOrganizationEnums: (state) => {
            state.enums.savePending = false;
        },
        setSaveOrganizationEnumsError: (state, action) => {
            state.enums.savePending = false;
            state.enums.error = action.payload;
        },

        startDeployments: (state) => {
            state.deployments.pending = true;
        },
        stopDeployments: (state) => {
            state.deployments.pending = false;
        },
        setDeployments: (state, action) => {
            const payload = action.payload || {};
            const items = Array.isArray(payload) ? payload : payload.items || [];
            const total = payload.total ?? items.length;

            state.deployments.pending = false;
            state.deployments.items = items;
            state.deployments.total = total;
            state.deployments.count = payload.count ?? items.length;
            state.deployments.currentPage = payload.currentPage ?? 1;
            state.deployments.totalPages = payload.totalPages ?? Math.max(1, Math.ceil((total || 0) / 10));
        },
        setDeploymentsError: (state, action) => {
            state.deployments.pending = false;
            state.deployments.error = action.payload;
        },
        deleteDeployment: (state, action) => {
            state.deployments.items = state.deployments.items.filter((deployment) => deployment.id !== action.payload.id);
            state.deployments.total = state.deployments.items.length;
            state.deployments.count = state.deployments.items.length;
        },
        resetDeployments: (state) => {
            state.deployments = createEntityState();
        },
        setDeploymentsCount: (state, action) => {
            state.deploymentsCount = action.payload;
        },
    },
});

export const {
    startIntegrations,
    stopIntegrations,
    setIntegrations,
    setIntegrationsError,
    addIntegration,
    editIntegration,
    deleteIntegration,
    setIntegrationTags,
    resetIntegrations,
    startSaveIntegrations,
    stopSaveIntegrations,
    setSaveIntegrationsError,
    startIntegrationChannels,
    stopIntegrationChannels,
    setIntegrationChannels,
    setIntegrationChannelsError,
    addIntegrationChannel,
    editIntegrationChannel,
    deleteIntegrationChannel,
    resetIntegrationChannels,
    startSaveIntegrationChannels,
    stopSaveIntegrationChannels,
    setSaveIntegrationChannelsError,
    startIntegrationJobs,
    stopIntegrationJobs,
    setIntegrationJobs,
    setIntegrationJobsError,
    addIntegrationJob,
    editIntegrationJob,
    deleteIntegrationJob,
    resetIntegrationJobs,
    startSaveIntegrationJobs,
    stopSaveIntegrationJobs,
    setSaveIntegrationJobsError,
    startIntegrationFunctions,
    stopIntegrationFunctions,
    setIntegrationFunctions,
    setIntegrationFunctionsError,
    addIntegrationFunction,
    editIntegrationFunction,
    deleteIntegrationFunction,
    resetIntegrationFunctions,
    startSaveIntegrationFunctions,
    stopSaveIntegrationFunctions,
    setSaveIntegrationFunctionsError,
    startOrganizationEnums,
    stopOrganizationEnums,
    setOrganizationEnums,
    setOrganizationEnumsError,
    addOrganizationEnum,
    editOrganizationEnum,
    deleteOrganizationEnum,
    resetOrganizationEnums,
    startSaveOrganizationEnums,
    stopSaveOrganizationEnums,
    setSaveOrganizationEnumsError,
    startDeployments,
    stopDeployments,
    setDeployments,
    setDeploymentsError,
    deleteDeployment,
    resetDeployments,
    setDeploymentsCount,
} = integrationSlice.actions;

export const getIntegrations = (state) => state.integration.integrations.items;
export const getIntegrationsTotal = (state) => state.integration.integrations.total;
export const getIntegrationsTotalPages = (state) => state.integration.integrations.totalPages;
export const getIntegrationsCurrentPage = (state) => state.integration.integrations.currentPage;
export const getIntegrationsPending = (state) => state.integration.integrations.pending;
export const getIntegrationsError = (state) => state.integration.integrations.error;
export const getIntegrationsSavePending = (state) => state.integration.integrations.savePending;
export const getIntegrationTags = (state) => state.integration.tags;

export const getIntegrationChannels = (state) => state.integration.channels.items;
export const getIntegrationChannelsTotal = (state) => state.integration.channels.total;
export const getIntegrationChannelsTotalPages = (state) => state.integration.channels.totalPages;
export const getIntegrationChannelsCurrentPage = (state) => state.integration.channels.currentPage;
export const getIntegrationChannelsPending = (state) => state.integration.channels.pending;
export const getIntegrationChannelsError = (state) => state.integration.channels.error;
export const getIntegrationChannelsSavePending = (state) => state.integration.channels.savePending;

export const getIntegrationJobs = (state) => state.integration.jobs.items;
export const getIntegrationJobsTotal = (state) => state.integration.jobs.total;
export const getIntegrationJobsTotalPages = (state) => state.integration.jobs.totalPages;
export const getIntegrationJobsCurrentPage = (state) => state.integration.jobs.currentPage;
export const getIntegrationJobsPending = (state) => state.integration.jobs.pending;
export const getIntegrationJobsError = (state) => state.integration.jobs.error;
export const getIntegrationJobsSavePending = (state) => state.integration.jobs.savePending;

export const getIntegrationFunctions = (state) => state.integration.functions.items;
export const getIntegrationFunctionsTotal = (state) => state.integration.functions.total;
export const getIntegrationFunctionsTotalPages = (state) => state.integration.functions.totalPages;
export const getIntegrationFunctionsCurrentPage = (state) => state.integration.functions.currentPage;
export const getIntegrationFunctionsPending = (state) => state.integration.functions.pending;
export const getIntegrationFunctionsError = (state) => state.integration.functions.error;
export const getIntegrationFunctionsSavePending = (state) => state.integration.functions.savePending;

export const getOrganizationEnums = (state) => state.integration.enums.items;
export const getOrganizationEnumsTotal = (state) => state.integration.enums.total;
export const getOrganizationEnumsTotalPages = (state) => state.integration.enums.totalPages;
export const getOrganizationEnumsCurrentPage = (state) => state.integration.enums.currentPage;
export const getOrganizationEnumsPending = (state) => state.integration.enums.pending;
export const getOrganizationEnumsError = (state) => state.integration.enums.error;
export const getOrganizationEnumsSavePending = (state) => state.integration.enums.savePending;

export const getDeployments = (state) => state.integration.deployments.items;
export const getDeploymentsTotal = (state) => state.integration.deployments.total;
export const getDeploymentsTotalPages = (state) => state.integration.deployments.totalPages;
export const getDeploymentsCurrentPage = (state) => state.integration.deployments.currentPage;
export const getDeploymentsPending = (state) => state.integration.deployments.pending;
export const getDeploymentsError = (state) => state.integration.deployments.error;
export const getDeploymentsCount = (state) => state.integration.deploymentsCount;

export const fetchIntegrationTagsAction = () => async (dispatch) => {
    try {
        api.get('integrations/tags')
            .then((response) => {
                dispatch(setIntegrationTags(response.data));
            })
            .catch((error) => {
                console.error(error);
            });
    } catch (e) {
        return console.error('error', e);
    }
};

export const fetchIntegrationsAction =
    ({ queryParams, onSuccess, onFailure } = {}) =>
        async (dispatch) => {
            try {
                dispatch(startIntegrations());
                const query = transformFiltersToQuery(queryParams);
                api.get(`integrations${query}`)
                    .then((response) => {
                        dispatch(setIntegrations(response.data));
                        if (onSuccess) onSuccess(response.data);
                        return response;
                    })
                    .catch((error) => {
                        dispatch(setIntegrationsError(error));
                        if (onFailure) onFailure(error);
                    });
            } catch (e) {
                dispatch(stopIntegrations());
                return console.error('error', e);
            }
        };

export const fetchIntegrationChannelsAction =
    ({ queryParams, onSuccess, onFailure } = {}) =>
        async (dispatch) => {
            try {
                dispatch(startIntegrationChannels());
                const query = transformFiltersToQuery(queryParams);
                api.get(`integration-channels${query}`)
                    .then((response) => {
                        dispatch(setIntegrationChannels(response.data));
                        if (onSuccess) onSuccess(response.data);
                        return response;
                    })
                    .catch((error) => {
                        dispatch(setIntegrationChannelsError(error));
                        if (onFailure) onFailure(error);
                    });
            } catch (e) {
                dispatch(stopIntegrationChannels());
                return console.error('error', e);
            }
        };

export const fetchIntegrationJobsAction =
    ({ queryParams, onSuccess, onFailure } = {}) =>
        async (dispatch) => {
            try {
                dispatch(startIntegrationJobs());
                const query = transformFiltersToQuery(queryParams);
                api.get(`integration-jobs${query}`)
                    .then((response) => {
                        dispatch(setIntegrationJobs(response.data));
                        if (onSuccess) onSuccess(response.data);
                        return response;
                    })
                    .catch((error) => {
                        dispatch(setIntegrationJobsError(error));
                        if (onFailure) onFailure(error);
                    });
            } catch (e) {
                dispatch(stopIntegrationJobs());
                return console.error('error', e);
            }
        };

export const fetchIntegrationFunctionsAction =
    ({ queryParams, onSuccess, onFailure } = {}) =>
        async (dispatch) => {
            try {
                dispatch(startIntegrationFunctions());
                const query = transformFiltersToQuery(queryParams);
                api.get(`integration-functions${query}`)
                    .then((response) => {
                        dispatch(setIntegrationFunctions(response.data));
                        if (onSuccess) onSuccess(response.data);
                        return response;
                    })
                    .catch((error) => {
                        dispatch(setIntegrationFunctionsError(error));
                        if (onFailure) onFailure(error);
                    });
            } catch (e) {
                dispatch(stopIntegrationFunctions());
                return console.error('error', e);
            }
        };

export const fetchOrganizationEnumsAction =
    ({ queryParams, onSuccess, onFailure } = {}) =>
        async (dispatch) => {
            try {
                dispatch(startOrganizationEnums());
                const query = transformFiltersToQuery(queryParams);
                api.get(`organization-enums${query}`)
                    .then((response) => {
                        dispatch(setOrganizationEnums(response.data));
                        if (onSuccess) onSuccess(response.data);
                        return response;
                    })
                    .catch((error) => {
                        dispatch(setOrganizationEnumsError(error));
                        if (onFailure) onFailure(error);
                    });
            } catch (e) {
                dispatch(stopOrganizationEnums());
                return console.error('error', e);
            }
        };

export const addIntegrationAction = (integrationDto, success, failure) => async (dispatch) => {
    try {
        dispatch(startSaveIntegrations());
        api.post('integrations', integrationDto)
            .then((response) => {
                dispatch(addIntegration(response.data));
                dispatch(fetchIntegrationTagsAction());
                if (success) success();
                return response;
            })
            .catch((error) => {
                console.error(error);
                dispatch(setSaveIntegrationsError(error));
                if (failure) failure(error);
            });
    } catch (e) {
        dispatch(stopSaveIntegrations());
        return console.error('error', e);
    }
};

export const editIntegrationAction = (id, integrationDto, success, failure) => async (dispatch) => {
    try {
        dispatch(startSaveIntegrations());
        api.put('integrations/' + id, integrationDto)
            .then((response) => {
                dispatch(editIntegration(response.data));
                dispatch(fetchIntegrationTagsAction());
                if (success) success();
                return response;
            })
            .catch((error) => {
                console.error(error);
                dispatch(setSaveIntegrationsError(error));
                if (failure) failure(error);
            });
    } catch (e) {
        dispatch(stopSaveIntegrations());
        return console.error('error', e);
    }
};

export const deleteIntegrationAction = (integrationId, success, failure) => async (dispatch) => {
    try {
        dispatch(startIntegrations());
        api.delete(`integrations/${integrationId}`)
            .then((response) => {
                dispatch(deleteIntegration(response.data));
                dispatch(fetchIntegrationTagsAction());
                if (success) success();
                return response;
            })
            .catch((error) => {
                dispatch(setIntegrationsError(error));
                if (failure) failure(error);
            });
    } catch (e) {
        dispatch(stopIntegrations());
        return console.error('error', e);
    }
};

export const addIntegrationChannelAction = (integrationDto, success, failure) => async (dispatch) => {
    try {
        dispatch(startSaveIntegrationChannels());
        api.post('integration-channels', integrationDto)
            .then((response) => {
                dispatch(addIntegrationChannel(response.data));
                if (success) success();
                return response;
            })
            .catch((error) => {
                console.error(error);
                dispatch(setSaveIntegrationChannelsError(error));
                if (failure) failure(error);
            });
    } catch (e) {
        dispatch(stopSaveIntegrationChannels());
        return console.error('error', e);
    }
};

export const editIntegrationChannelAction = (id, integrationDto, success, failure) => async (dispatch) => {
    try {
        dispatch(startSaveIntegrationChannels());
        api.put('integration-channels/' + id, integrationDto)
            .then((response) => {
                dispatch(editIntegrationChannel(response.data));
                if (success) success();
                return response;
            })
            .catch((error) => {
                console.error(error);
                dispatch(setSaveIntegrationChannelsError(error));
                if (failure) failure(error);
            });
    } catch (e) {
        dispatch(stopSaveIntegrationChannels());
        return console.error('error', e);
    }
};

export const deleteIntegrationChannelAction = (integrationId, success, failure) => async (dispatch) => {
    try {
        dispatch(startIntegrationChannels());
        api.delete(`integration-channels/${integrationId}`)
            .then((response) => {
                dispatch(deleteIntegrationChannel(response.data));
                if (success) success();
                return response;
            })
            .catch((error) => {
                dispatch(setIntegrationChannelsError(error));
                if (failure) failure(error);
            });
    } catch (e) {
        dispatch(stopIntegrationChannels());
        return console.error('error', e);
    }
};

export const addIntegrationJobAction = (integrationDto, success, failure) => async (dispatch) => {
    try {
        dispatch(startSaveIntegrationJobs());
        api.post('integration-jobs', integrationDto)
            .then((response) => {
                dispatch(addIntegrationJob(response.data));
                if (success) success();
                return response;
            })
            .catch((error) => {
                console.error(error);
                dispatch(setSaveIntegrationJobsError(error));
                if (failure) failure(error);
            });
    } catch (e) {
        dispatch(stopSaveIntegrationJobs());
        return console.error('error', e);
    }
};

export const editIntegrationJobAction = (id, integrationDto, success, failure) => async (dispatch) => {
    try {
        dispatch(startSaveIntegrationJobs());
        api.put('integration-jobs/' + id, integrationDto)
            .then((response) => {
                dispatch(editIntegrationJob(response.data));
                if (success) success();
                return response;
            })
            .catch((error) => {
                console.error(error);
                dispatch(setSaveIntegrationJobsError(error));
                if (failure) failure(error);
            });
    } catch (e) {
        dispatch(stopSaveIntegrationJobs());
        return console.error('error', e);
    }
};

export const deleteIntegrationJobAction = (integrationId, success, failure) => async (dispatch) => {
    try {
        dispatch(startIntegrationJobs());
        api.delete(`integration-jobs/${integrationId}`)
            .then((response) => {
                dispatch(deleteIntegrationJob(response.data));
                if (success) success();
                return response;
            })
            .catch((error) => {
                dispatch(setIntegrationJobsError(error));
                if (failure) failure(error);
            });
    } catch (e) {
        dispatch(stopIntegrationJobs());
        return console.error('error', e);
    }
};

export const addIntegrationFunctionAction = (integrationDto, success, failure) => async (dispatch) => {
    try {
        dispatch(startSaveIntegrationFunctions());
        api.post('integration-functions', integrationDto)
            .then((response) => {
                dispatch(addIntegrationFunction(response.data));
                if (success) success(response.data);
                return response;
            })
            .catch((error) => {
                console.error(error);
                dispatch(setSaveIntegrationFunctionsError(error));
                if (failure) failure(error);
            });
    } catch (e) {
        dispatch(stopSaveIntegrationFunctions());
        return console.error('error', e);
    }
};

export const editIntegrationFunctionAction = (id, integrationDto, success, failure) => async (dispatch) => {
    try {
        dispatch(startSaveIntegrationFunctions());
        api.put('integration-functions/' + id, integrationDto)
            .then((response) => {
                dispatch(editIntegrationFunction(response.data));
                if (success) success(response.data);
                return response;
            })
            .catch((error) => {
                console.error(error);
                dispatch(setSaveIntegrationFunctionsError(error));
                if (failure) failure(error);
            });
    } catch (e) {
        dispatch(stopSaveIntegrationFunctions());
        return console.error('error', e);
    }
};

export const deleteIntegrationFunctionAction = (integrationId, success, failure) => async (dispatch) => {
    try {
        dispatch(startIntegrationFunctions());
        api.delete(`integration-functions/${integrationId}`)
            .then((response) => {
                dispatch(deleteIntegrationFunction(response.data));
                if (success) success();
                return response;
            })
            .catch((error) => {
                dispatch(setIntegrationFunctionsError(error));
                if (failure) failure(error);
            });
    } catch (e) {
        dispatch(stopIntegrationFunctions());
        return console.error('error', e);
    }
};

export const addOrganizationEnumAction = (integrationDto, success, failure) => async (dispatch) => {
    try {
        dispatch(startSaveOrganizationEnums());
        api.post('organization-enums', integrationDto)
            .then((response) => {
                dispatch(addOrganizationEnum(response.data));
                if (success) success();
                return response;
            })
            .catch((error) => {
                console.error(error);
                dispatch(setSaveOrganizationEnumsError(error));
                if (failure) failure(error);
            });
    } catch (e) {
        dispatch(stopSaveOrganizationEnums());
        return console.error('error', e);
    }
};

export const editOrganizationEnumAction = (id, integrationDto, success, failure) => async (dispatch) => {
    try {
        dispatch(startSaveOrganizationEnums());
        api.put('organization-enums/' + id, integrationDto)
            .then((response) => {
                dispatch(editOrganizationEnum(response.data));
                if (success) success();
                return response;
            })
            .catch((error) => {
                console.error(error);
                dispatch(setSaveOrganizationEnumsError(error));
                if (failure) failure(error);
            });
    } catch (e) {
        dispatch(stopSaveOrganizationEnums());
        return console.error('error', e);
    }
};

export const deleteOrganizationEnumAction = (integrationId, success, failure) => async (dispatch) => {
    try {
        dispatch(startOrganizationEnums());
        api.delete(`organization-enums/${integrationId}`)
            .then((response) => {
                dispatch(deleteOrganizationEnum(response.data));
                if (success) success();
                return response;
            })
            .catch((error) => {
                dispatch(setOrganizationEnumsError(error));
                if (failure) failure(error);
            });
    } catch (e) {
        dispatch(stopOrganizationEnums());
        return console.error('error', e);
    }
};

export const fetchDeploymentsAction = ({ queryParams, onSuccess, onFailure } = {}) => async (dispatch) => {
    try {
        dispatch(startDeployments());
        api.get('deployments', { params: queryParams })
            .then((response) => {
                dispatch(setDeployments(response.data));
                if (onSuccess) onSuccess(response.data);
                return response;
            })
            .catch((error) => {
                dispatch(setDeploymentsError(error));
                if (onFailure) onFailure(error);
            });
    } catch (e) {
        dispatch(stopDeployments());
        return console.error('error', e);
    }
};

export const fetchDeploymentsCountAction = ({ queryParams, onSuccess, onFailure } = {}) => async (dispatch) => {
    try {
        api.get('deployments/count', { params: queryParams })
            .then((response) => {
                const count = response.data?.count ?? response.data?.total ?? response.data ?? 0;
                dispatch(setDeploymentsCount(Number(count) || 0));
                if (onSuccess) onSuccess(response.data);
                return response;
            })
            .catch((error) => {
                dispatch(setDeploymentsError(error));
                if (onFailure) onFailure(error);
            });
    } catch (e) {
        return console.error('error', e);
    }
};

export const approveDeploymentAction = (deploymentId, success, failure) => async (dispatch) => {
    try {
        api.post(`deployments/${deploymentId}/approve`, { approvedBy: 'backoffice' })
            .then((response) => {
                if (success) success(response.data);
                return response;
            })
            .catch((error) => {
                if (failure) failure(error);
            });
    } catch (error) {
        console.error('Error approving deployment.', error);
        throw error;
    }
};

export const rejectDeploymentAction = (deploymentId, reason = 'Rejected from backoffice', success, failure) => async (dispatch) => {
    try {
        api.post(`deployments/${deploymentId}/reject`, { rejectedBy: 'backoffice', reason })
            .then((response) => {
                if (success) success(response.data);
                return response;
            })
            .catch((error) => {
                if (failure) failure(error);
            });
    } catch (error) {
        console.error('Error rejecting deployment.', error);
        throw error;
    }
};

export const applyDeploymentAction = (deploymentId, success, failure) => async (dispatch) => {
    try {
        api.post(`deployments/${deploymentId}/apply`)
            .then((response) => {
                if (success) success(response.data);
                return response;
            })
            .catch((error) => {
                if (failure) failure(error);
            });
    } catch (error) {
        console.error('Error applying deployment.', error);
        throw error;
    }
};

export const previewIntegrationAction = (payload, success, failure) => async () => {
    try {
        api.post('integrations/preview', payload)
            .then((response) => {
                if (success) success(response.data);
                return response;
            })
            .catch((error) => {
                if (failure) failure(error);
            });
    } catch (error) {
        console.error('Error previewing integration pipeline.', error);
        throw error;
    }
};

export const detectEnvironmentVariablesAction = (pipeline, success, failure) => async () => {
    try {
        api.post('integrations/environment/detect', { pipeline })
            .then((response) => {
                if (success) success(response.data);
                return response;
            })
            .catch((error) => {
                if (failure) failure(error);
            });
    } catch (error) {
        console.error('Error detecting environment variables.', error);
        if (failure) failure(error);
    }
};

export const fetchTargetResourceAction = ({ resourceType, resourceId, success, failure, target = true }) => async () => {
    try {
        api.get(`deployments/${resourceType}/${resourceId}/${target ? 'target-' : ''}resource`)
            .then((response) => {
                if (success) success(response.data);
                return response;
            })
            .catch((error) => {
                if (failure) failure(error);
            });
    } catch (error) {
        console.error('Error fetching target resource.', error);
        if (failure) failure(error);
    }
};

export const deployResourceAction = (resourceType, resourceId, deployConfig = {}, success, failure) => async () => {
    try {
        const payload = {
            resourceType,
            resourceId,
            ...deployConfig,
        };

        api.post('deployments/deploy', payload)
            .then((response) => {
                if (success) success(response.data);
                return response;
            })
            .catch((error) => {
                if (failure) failure(error);
            });
    } catch (error) {
        console.error('Error deploying resource.', error);
        throw error;
    }
};

export default integrationSlice.reducer;
